/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { GtinValidationSetService } from "../../services/gtin-validation-set.service";
import { OnInit, Component, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";

import { RESULTS_PER_PAGE } from "app/app-constants";
import { ConfirmDeleteDialogComponent } from "../confirm-delete-dialog.component";
import { LoadGtinValidationSetDialogComponent } from "./load-gtin-validation-set.dialog.component";
import { ViewGtinValidationSetDialogComponent } from "./view-gtin-validation-set.dialog.component";
import { GtinValidationSetHeader } from "app/models/gtin-validation-set-header";
import { PagedResults } from "app/models/paged-results";

@Component({
  templateUrl: './gtin-validation-set.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class GtinValidationSetComponent implements OnInit {

  /** Dialog used to import a file as a new GTIN Validation Set */
  @ViewChild('gtinDialog', { static: false })
  mGtinDialogComponent: LoadGtinValidationSetDialogComponent;

  /** Dialog used to import a file as a new GTIN Validation Set */
  @ViewChild('viewGtinDialog', { static: false })
  mViewGtinDialogComponent: ViewGtinValidationSetDialogComponent;

  @ViewChild('confirmDeleteDialog')
  confirmDialog: ConfirmDeleteDialogComponent;

  /** GTIN Validation Sets being displayed in the UI */
  gtinHeaders: GtinValidationSetHeader[] = [];

  /* Paging Variables */
  numberOfGtin = 0;
  totalNumberOfGtin = 0;
  currentPageForGtin = 1;
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForGtin: number = this.resultsPerPageValues[0];

  /** Constructor */
  constructor(
    private gtinService: GtinValidationSetService,
    private mRoute: ActivatedRoute,
  ) { }

  filterColumnGtin = null;
  filterColumnNameGtin = "";
  filterStringGtin = "";

  /**
   * Angular Life Cycle Method.  Called when the component is being initialized.
   * Sets some default values and starts loading the GTIN Header data (TODO: Move this to a resolver)
   */
  ngOnInit(): void {

    // Subscribe to the Route to get the Rules that get loaded as it is resolved
    this.mRoute.data.subscribe((data: { gtinvalidationsetheader: PagedResults<GtinValidationSetHeader> }) => {
      //Set the initial values for Pagination
      const results = data.gtinvalidationsetheader;
      this.gtinHeaders.push(...results.data);
      this.totalNumberOfGtin = results.size;
    console.log("this.totalNumberOfGtin"+this.totalNumberOfGtin);
    });
  }

  /**
   * Refreshes the page of GTIN Headers being displayed
   */
  private async refreshGtinHeaders(): Promise<void> {
    // Make the API call
    let page: PagedResults<GtinValidationSetHeader> = null;
    try {
      page = await this.gtinService.getGtinValidationSetHeaders((this.currentPageForGtin - 1) * this.selectedPageSizeValueForGtin, this.selectedPageSizeValueForGtin, 'ASC', this.filterStringGtin);
    } catch (error) {
      this.handleError(error);
      return;
    }

    // Clear the current list
    while (this.gtinHeaders.length > 0) {
      this.gtinHeaders.pop();
    }

    // Add the data for the current list
    if (page.data?.length > 0) {
      this.gtinHeaders.push(...page.data);
    }

    // Update the total size for paging
    this.totalNumberOfGtin = page.size;
  }
  
  /**
   * Displays the error to the User
   */
  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      alert(error.error);
    } 
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

  /**
   * Called when the User changes the "Results Per Page" value.
   * Refreshes the GTIN Headers being displayed to include the new page size.
   */
  manageResultPerPageClickedForGtin(value: number): void {
    this.selectedPageSizeValueForGtin = value;

    // Selecting any value will bring you back to first page
    this.currentPageForGtin = 1;
    this.refreshGtinHeaders();
  }


  importValidationClicked (): void {
    this.mGtinDialogComponent.show("IMPORT");
  }  

  addClicked (): void{
    this.mGtinDialogComponent.show("ADD");
  }

  viewGtinValidationSetClicked(name: string ): void {
    this.mViewGtinDialogComponent.show(name);
  }

  setFilterGtinValidationSet(filterColumnName: string, filterColumn: string): void {
    this.filterColumnGtin = filterColumn;
    this.filterColumnNameGtin = filterColumnName;
    if (!this.filterColumnGtin) {
      this.filterStringGtin = "";
    }
    this.filterStringChangedGtin(1);
  }

  filterStringChangedGtin(pageNumber?: number): void {
    if (!pageNumber) { pageNumber = 1; }
    // Show only size on this new page
    console.log("page Changed to " + pageNumber);
    this.currentPageForGtin = pageNumber;

    this.refreshGtinHeaders();

  }

  pageChanged(pageNumber: number): void {
    console.log("pageNumber"+pageNumber);
    if(pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }
    if (this.currentPageForGtin == pageNumber) {
      return;
    }
    this.currentPageForGtin = pageNumber;
    this.refreshGtinHeaders();
  }
  
  selectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.gtinHeaders.forEach(header => header.selected = checked);
  }

  isAllSelected(): boolean {
    return this.gtinHeaders.every(header => header.selected);
  }

  isEmptySelection(): boolean {
    return this.gtinHeaders.filter(header => header.selected).length === 0;
  }


  onDelete(): void {
  if (!this.isEmptySelection()) {
    const names: string[] = this.gtinHeaders
      .filter(header => header.selected)
      .map(header => header.name);

    this.confirmDialog.show("Delete Validation Sets", names);
  }
}

getSelectedGtinIds(): number[] {
  return this.gtinHeaders
    .filter(header => header.selected)
    .map(header => header.id);
}


deleteGtinValidationSets(): void {
  const selectedIds = this.getSelectedGtinIds();

  this.gtinService.deleteValidationSets(selectedIds).then(
    response => {
      this.refreshGtinHeaders(); // Refresh the list
    },
    error => {
      this.handleError(error); // Handle any errors
    }
  );
}


}

